/**
 * TBMuktBharat Search Page
 * Fast & lightweight - Uses GovLayout components
 */

import React, { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search as SearchIcon,
  Users,
  UsersRound,
  X,
  Clock,
  UserPlus,
  Check,
  Loader2
} from 'lucide-react'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  GovLayout,
  GovPageTitle,
  GovCard,
  GovEmptyState
} from '@/components/layout/GovLayout'

// ================================================================================
// TYPES
// ================================================================================
interface User {
  id: number
  uuid: string
  firstName: string
  lastName: string
  fullName: string
  username: string
  profileImage: string | null
  bio?: string
}

interface Group {
  id: number
  name: string
  description?: string
  coverImage?: string
  memberCount: number
}

interface RecentSearch {
  query: string
  timestamp: string
}

// ================================================================================
// USER CARD COMPONENT
// ================================================================================
interface UserCardProps {
  user: User
  isCurrentUser: boolean
  friendStatus: string | undefined
  isRequestSent: boolean
  onUserClick: () => void
  onSendRequest: () => void
}

const UserCard = memo(({ user, isCurrentUser, friendStatus, isRequestSent, onUserClick, onSendRequest }: UserCardProps) => {
  if (isCurrentUser) return null

  const getFriendButton = () => {
    if (friendStatus === 'FRIENDS') {
      return (
        <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
          <Check className="w-3 h-3" />
          Friends
        </span>
      )
    }

    if (friendStatus === 'PENDING_SENT' || isRequestSent) {
      return (
        <span className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      )
    }

    if (friendStatus === 'PENDING_RECEIVED') {
      return (
        <span className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-full">
          Accept
        </span>
      )
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSendRequest()
        }}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-full hover:bg-blue-700"
      >
        <UserPlus className="w-3 h-3" />
        Add
      </button>
    )
  }

  return (
    <div
      onClick={onUserClick}
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
    >
      <img
        src={user.profileImage || '/default-avatar.svg'}
        alt={user.fullName}
        className="w-12 h-12 rounded-full object-cover"
        loading="lazy"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{user.fullName}</p>
        <p className="text-sm text-gray-500">@{user.username}</p>
        {user.bio && <p className="text-sm text-gray-400 truncate">{user.bio}</p>}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        {getFriendButton()}
      </div>
    </div>
  )
})

UserCard.displayName = 'UserCard'

// ================================================================================
// GROUP CARD COMPONENT
// ================================================================================
interface GroupCardProps {
  group: Group
  onGroupClick: () => void
}

const GroupCard = memo(({ group, onGroupClick }: GroupCardProps) => (
  <div
    onClick={onGroupClick}
    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
  >
    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center flex-shrink-0">
      <UsersRound className="w-6 h-6 text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{group.name}</p>
      {group.description && <p className="text-sm text-gray-500 truncate">{group.description}</p>}
      <p className="text-xs text-gray-400 mt-1">{group.memberCount || 0} members</p>
    </div>
  </div>
))

GroupCard.displayName = 'GroupCard'

// ================================================================================
// RECENT SEARCH ITEM
// ================================================================================
interface RecentSearchItemProps {
  search: RecentSearch
  onClick: () => void
}

const RecentSearchItem = memo(({ search, onClick }: RecentSearchItemProps) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
  >
    <Clock className="w-4 h-4 text-gray-400" />
    <span>{search.query}</span>
  </button>
))

RecentSearchItem.displayName = 'RecentSearchItem'

// ================================================================================
// MAIN SEARCH PAGE
// ================================================================================
const SearchPage = () => {
  const { user: currentUser } = useAuthStore()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'groups'>('all')
  const [loading, setLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])
  const [results, setResults] = useState<{ users: User[]; groups: Group[] }>({ users: [], groups: [] })
  const [sentRequests, setSentRequests] = useState<Set<number>>(new Set())
  const [friendStatuses, setFriendStatuses] = useState<Map<number, string>>(new Map())

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch {
        localStorage.removeItem('recentSearches')
      }
    }
  }, [])

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= 1) {
        performSearch(query)
      }
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [query, activeTab])

  // Check friend status for displayed users
  useEffect(() => {
    results.users.forEach(user => {
      if (user.id !== currentUser?.id && !friendStatuses.has(user.id)) {
        checkFriendStatus(user.id)
      }
    })
  }, [results.users, currentUser?.id])

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults({ users: [], groups: [] })
      return
    }

    setLoading(true)
    try {
      const usersPromise = (activeTab === 'all' || activeTab === 'users')
        ? api.get(`/users/search?keyword=${encodeURIComponent(searchQuery)}&page=0&size=10`)
        : Promise.resolve({ data: { success: true, data: { content: [] } } })

      const groupsPromise = (activeTab === 'all' || activeTab === 'groups')
        ? api.get(`/groups/public?page=0&size=10`)
        : Promise.resolve({ data: { success: true, data: { content: [] } } })

      const [usersRes, groupsRes] = await Promise.all([usersPromise, groupsPromise])

      const users = usersRes.data.success ? (usersRes.data.data?.content || []) : []
      const groups = groupsRes.data.success ? (groupsRes.data.data?.content || []) : []

      const filteredGroups = groups.filter((g: Group) =>
        g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )

      setResults({
        users: users.slice(0, 10),
        groups: filteredGroups.slice(0, 10)
      })
    } catch (error) {
      console.error('Search error:', error)
      setResults({ users: [], groups: [] })
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  const checkFriendStatus = useCallback(async (userId: number) => {
    try {
      const res = await api.get(`/friends/status/${userId}`)
      if (res.data.success) {
        setFriendStatuses(prev => new Map(prev).set(userId, res.data.data))
      }
    } catch (error) {
      console.error('Error checking friend status:', error)
    }
  }, [])

  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return

    const updated = [
      { query: searchQuery, timestamp: new Date().toISOString() },
      ...recentSearches.filter(s => s.query !== searchQuery)
    ].slice(0, 10)

    setRecentSearches(updated)
    localStorage.setItem('recentSearches', JSON.stringify(updated))
  }, [recentSearches])

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([])
    localStorage.removeItem('recentSearches')
  }, [])

  const sendFriendRequest = useCallback(async (userId: number) => {
    try {
      await api.post(`/friends/request/${userId}`)
      setSentRequests(prev => new Set([...prev, userId]))
    } catch (error) {
      console.error('Error sending friend request:', error)
    }
  }, [])

  const handleUserClick = useCallback((user: User) => {
    saveRecentSearch(query)
    navigate(`/profile/${user.username}`)
  }, [query, navigate, saveRecentSearch])

  const handleGroupClick = useCallback((group: Group) => {
    saveRecentSearch(query)
    navigate(`/groups/${group.id}`)
  }, [query, navigate, saveRecentSearch])

  const handleRecentSearchClick = useCallback((searchQuery: string) => {
    setQuery(searchQuery)
    performSearch(searchQuery)
  }, [performSearch])

  const tabs = [
    { id: 'all' as const, label: 'All', icon: SearchIcon },
    { id: 'users' as const, label: 'Users', icon: Users },
    { id: 'groups' as const, label: 'Groups', icon: UsersRound }
  ]

  const totalResults = results.users.length + results.groups.length

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Search" subtitle="Find users and groups" icon={SearchIcon} />

        {/* Search Input */}
        <GovCard>
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type to search users, groups..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="w-full pl-12 pr-10 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('')
                  setResults({ users: [], groups: [] })
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {loading && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 pb-2 px-3 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </GovCard>

        {/* Recent Searches */}
        {query.trim().length === 0 && recentSearches.length > 0 && (
          <GovCard>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-gray-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Recent Searches</span>
              </div>
              <button
                onClick={clearRecentSearches}
                className="text-xs text-blue-600 hover:underline"
              >
                Clear All
              </button>
            </div>
            <div className="space-y-1">
              {recentSearches.slice(0, 5).map((search, index) => (
                <RecentSearchItem
                  key={`${search.query}-${index}`}
                  search={search}
                  onClick={() => handleRecentSearchClick(search.query)}
                />
              ))}
            </div>
          </GovCard>
        )}

        {/* Results */}
        {query.trim().length > 0 && (
          <div className="space-y-4">
            {/* No Results */}
            {!loading && totalResults === 0 && (
              <GovEmptyState
                icon="🔍"
                title="No results found"
                description={`No matches for "${query}"`}
              />
            )}

            {/* Users Results */}
            {(activeTab === 'all' || activeTab === 'users') && results.users.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-600">
                    Users ({results.users.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {results.users.map((user) => (
                    <UserCard
                      key={user.id}
                      user={user}
                      isCurrentUser={user.id === currentUser?.id}
                      friendStatus={friendStatuses.get(user.id)}
                      isRequestSent={sentRequests.has(user.id)}
                      onUserClick={() => handleUserClick(user)}
                      onSendRequest={() => sendFriendRequest(user.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Groups Results */}
            {(activeTab === 'all' || activeTab === 'groups') && results.groups.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <UsersRound className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-600">
                    Groups ({results.groups.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {results.groups.map((group) => (
                    <GroupCard
                      key={group.id}
                      group={group}
                      onGroupClick={() => handleGroupClick(group)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {query.trim().length === 0 && recentSearches.length === 0 && (
          <GovEmptyState
            icon="🔍"
            title="Search"
            description="Find users and groups. Start typing to see results."
          />
        )}
      </div>
    </GovLayout>
  )
}

export default memo(SearchPage)